import type { Uploadable } from '@/models/uploadable';
import { canParseAsUrl } from '@/utils/parse-url';
import { convertUploadableToBlob } from '@/utils/uploadable';

export interface MediaMetadata {
  width?: number;
  height?: number;
  time?: number;
}

export function getMediaMetadata(input: Uploadable): Promise<MediaMetadata> {
  if (!canParseAsUrl(input.url)) return Promise.reject('BAD_MEDIA_URL');

  const { searchParams } = new URL(input.url);

  if (searchParams.has('h') || searchParams.has('t')) {
    const width = Number.parseInt(searchParams.get('w') ?? '0');
    const height = Number.parseInt(searchParams.get('h') ?? '0');
    const time = Number.parseInt(searchParams.get('t') ?? '0');

    return Promise.resolve({
      width,
      height,
      time,
    });
  }

  const category = input.type.split('/')[0];

  switch (category) {
    case 'image':
      return getImageMetadata(input);
    case 'video':
      return getVideoMetadata(input);
    case 'audio':
      return getAudioMetadata(input);
    default:
      return Promise.resolve({});
  }
}

export async function getImageMetadata(input: Uploadable): Promise<MediaMetadata> {
  const blobUrl = URL.createObjectURL(await convertUploadableToBlob(input));

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = () => {
      reject('IMAGE_LOAD_ERROR');
    };
    img.src = blobUrl;
  });
}

export async function getVideoMetadata(input: Uploadable): Promise<MediaMetadata> {
  const blobUrl = URL.createObjectURL(await convertUploadableToBlob(input));

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(blobUrl);
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        time: Math.round(video.duration),
      });
    };
    video.onerror = () => {
      reject('VIDEO_LOAD_ERROR');
    };
    video.src = blobUrl;
  });
}

export async function getAudioMetadata(input: Uploadable): Promise<MediaMetadata> {
  const blobUrl = URL.createObjectURL(await convertUploadableToBlob(input));

  return new Promise((resolve, reject) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(blobUrl);
      resolve({
        time: Math.round(audio.duration),
      });
    };
    audio.onerror = () => {
      reject('AUDIO_LOAD_ERROR');
    };
    audio.src = blobUrl;
  });
}
